import * as THREE from 'three';
import { SKY_COLOR, NETHER_SKY_COLOR } from '../constants.js';

/**
 * CaveFog - Underground atmosphere system with dynamic fog and dust particles.
 *
 * Adjusts scene fog density/color and spawns floating dust motes when the
 * player is underground, creating a moody cave ambiance.
 *
 * Behaviors:
 *   - Underground (isUnderground = true):
 *     * Gradually tightens fog (near/far decrease)
 *     * Darkens fog color to deep gray-brown
 *     * Spawns floating dust particles (tiny drifting specks)
 *     * Deeper Y = denser fog
 *
 *   - Near lava underground:
 *     * Fog tint shifts to warm orange
 *     * Ember-like orange particles join the dust
 *
 *   - Nether dimension (currentDimension = 'nether'):
 *     * Preserves existing red fog
 *     * Adds orange ember particles
 *
 *   - All transitions lerp smoothly over ~1 second
 *
 * Self-contained: only imports THREE.
 */

// --- Configuration ---
const DUST_COUNT = 40;                  // dust particle pool size
const EMBER_COUNT = 15;                 // ember particle pool (shared pool, total = DUST + EMBER)
const TOTAL_PARTICLES = DUST_COUNT + EMBER_COUNT;

const DUST_SPAWN_RADIUS = 10;          // horizontal spawn range around player
const DUST_SPAWN_HEIGHT = 6;           // vertical spawn range above/below player
const DUST_DRIFT_SPEED = 0.3;          // base drift speed
const DUST_LIFETIME_MIN = 3.0;
const DUST_LIFETIME_MAX = 6.0;
const DUST_SIZE = 0.06;

const EMBER_DRIFT_SPEED = 0.6;
const EMBER_LIFETIME_MIN = 1.5;
const EMBER_LIFETIME_MAX = 3.5;
const EMBER_SIZE = 0.05;

// Fog target values when underground
const CAVE_FOG_NEAR_SHALLOW = 15;      // at Y ~35 (just below surface)
const CAVE_FOG_NEAR_DEEP = 3;          // at Y ~0 (deep underground)
const CAVE_FOG_FAR_SHALLOW = 60;
const CAVE_FOG_FAR_DEEP = 25;

// Underground threshold (same as main.js uses)
const UNDERGROUND_Y_THRESHOLD = 40;

// Transition speed (units per second for lerp factor)
const TRANSITION_SPEED = 1.5;

// Colors
const CAVE_FOG_COLOR = new THREE.Color(0x1A1510);       // dark brown-gray
const LAVA_FOG_COLOR = new THREE.Color(0x2D1508);       // warm dark orange-brown
const NETHER_FOG_COLOR = new THREE.Color(NETHER_SKY_COLOR);      // existing nether red

// Surface (overworld) fog defaults - restored when leaving caves
const SURFACE_FOG_COLOR = new THREE.Color(SKY_COLOR);
const SURFACE_FOG_NEAR = 50;
// SURFACE_FOG_FAR is read from scene.fog at construction time

export class CaveFog {
  /**
   * @param {THREE.Scene} scene - The main Three.js scene
   */
  constructor(scene) {
    this.scene = scene;

    // Read initial fog settings from the scene as our "surface" defaults
    this._surfaceFogFar = scene.fog ? scene.fog.far : 150;

    // Current interpolation factor: 0 = surface, 1 = fully underground
    this._caveFactor = 0;
    this._targetCaveFactor = 0;

    // Lava proximity factor: 0 = no lava, 1 = near lava
    this._lavaFactor = 0;
    this._targetLavaFactor = 0;

    // Nether state
    this._inNether = false;

    // Track if we are currently managing fog (to avoid fighting with other systems)
    this._managingFog = false;

    // Depth ratio cache (0 = just below threshold, 1 = bedrock level)
    this._depthRatio = 0;

    // Temp color for lerping
    this._tmpColor = new THREE.Color();
    this._currentFogColor = new THREE.Color();

    // --- Dust / Ember Particles ---
    this._buildParticles();

    // Player tracking
    this._playerPos = new THREE.Vector3();

    // Spawn timers
    this._dustSpawnTimer = 0;
    this._dustSpawnInterval = 0.12;     // spawn a dust particle every ~120ms
    this._emberSpawnTimer = 0;
    this._emberSpawnInterval = 0.25;
  }

  // ====================================================================
  // Particle System
  // ====================================================================

  _buildParticles() {
    // Create a soft round texture for dust/ember sprites
    const canvas = document.createElement('canvas');
    canvas.width = 16;
    canvas.height = 16;
    const ctx = canvas.getContext('2d');
    const grad = ctx.createRadialGradient(8, 8, 0, 8, 8, 8);
    grad.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
    grad.addColorStop(0.5, 'rgba(255, 255, 255, 0.3)');
    grad.addColorStop(1, 'rgba(255, 255, 255, 0.0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 16, 16);
    this._particleTexture = new THREE.CanvasTexture(canvas);

    // Parallel arrays for particle state
    this._positions = new Float32Array(TOTAL_PARTICLES * 3);
    this._colors = new Float32Array(TOTAL_PARTICLES * 3);
    this._sizes = new Float32Array(TOTAL_PARTICLES);
    this._velocities = new Float32Array(TOTAL_PARTICLES * 3);
    this._lifetimes = new Float32Array(TOTAL_PARTICLES);
    this._maxLifetimes = new Float32Array(TOTAL_PARTICLES);
    this._particleTypes = new Uint8Array(TOTAL_PARTICLES); // 0 = dust, 1 = ember

    // Initialize all as dead
    for (let i = 0; i < TOTAL_PARTICLES; i++) {
      this._lifetimes[i] = 0;
      this._sizes[i] = 0;
      this._positions[i * 3] = 0;
      this._positions[i * 3 + 1] = -1000;
      this._positions[i * 3 + 2] = 0;
      this._colors[i * 3] = 0.7;
      this._colors[i * 3 + 1] = 0.7;
      this._colors[i * 3 + 2] = 0.7;
    }

    this._geo = new THREE.BufferGeometry();
    this._geo.setAttribute('position', new THREE.BufferAttribute(this._positions, 3));
    this._geo.setAttribute('color', new THREE.BufferAttribute(this._colors, 3));
    this._geo.setAttribute('size', new THREE.BufferAttribute(this._sizes, 1));

    this._mat = new THREE.PointsMaterial({
      map: this._particleTexture,
      vertexColors: true,
      size: DUST_SIZE,
      transparent: true,
      opacity: 0.6,
      depthWrite: false,
      sizeAttenuation: true,
      blending: THREE.NormalBlending,
    });

    this._points = new THREE.Points(this._geo, this._mat);
    this._points.frustumCulled = false;
    this._points.visible = false;
    this.scene.add(this._points);

    this._elapsed = 0;
  }

  /**
   * Spawn a dust particle near the player.
   */
  _spawnDust() {
    // Find a dead slot in the dust range [0, DUST_COUNT)
    for (let i = 0; i < DUST_COUNT; i++) {
      if (this._lifetimes[i] <= 0) {
        this._initParticle(i, 'dust');
        return;
      }
    }
  }

  /**
   * Spawn an ember particle near the player.
   */
  _spawnEmber() {
    // Find a dead slot in the ember range [DUST_COUNT, TOTAL_PARTICLES)
    for (let i = DUST_COUNT; i < TOTAL_PARTICLES; i++) {
      if (this._lifetimes[i] <= 0) {
        this._initParticle(i, 'ember');
        return;
      }
    }
  }

  /**
   * Initialize a particle in the given slot.
   * @param {number} idx - Particle index
   * @param {'dust'|'ember'} type
   */
  _initParticle(idx, type) {
    const i3 = idx * 3;
    const px = this._playerPos.x;
    const py = this._playerPos.y;
    const pz = this._playerPos.z;

    // Random position around player
    this._positions[i3]     = px + (Math.random() - 0.5) * DUST_SPAWN_RADIUS * 2;
    this._positions[i3 + 1] = py + (Math.random() - 0.5) * DUST_SPAWN_HEIGHT * 2;
    this._positions[i3 + 2] = pz + (Math.random() - 0.5) * DUST_SPAWN_RADIUS * 2;

    if (type === 'dust') {
      // Slow random drift
      const speed = DUST_DRIFT_SPEED;
      this._velocities[i3]     = (Math.random() - 0.5) * speed;
      this._velocities[i3 + 1] = (Math.random() - 0.5) * speed * 0.5; // less vertical
      this._velocities[i3 + 2] = (Math.random() - 0.5) * speed;

      // Gray-white color with slight variation
      const gray = 0.55 + Math.random() * 0.3;
      this._colors[i3]     = gray;
      this._colors[i3 + 1] = gray * 0.95;
      this._colors[i3 + 2] = gray * 0.9;

      this._sizes[idx] = DUST_SIZE * (0.7 + Math.random() * 0.6);

      const life = DUST_LIFETIME_MIN + Math.random() * (DUST_LIFETIME_MAX - DUST_LIFETIME_MIN);
      this._lifetimes[idx] = life;
      this._maxLifetimes[idx] = life;
      this._particleTypes[idx] = 0;

    } else {
      // Ember: rises upward, orange-yellow
      const speed = EMBER_DRIFT_SPEED;
      this._velocities[i3]     = (Math.random() - 0.5) * speed * 0.6;
      this._velocities[i3 + 1] = speed * (0.5 + Math.random() * 0.5); // mostly upward
      this._velocities[i3 + 2] = (Math.random() - 0.5) * speed * 0.6;

      // Orange-yellow ember color
      this._colors[i3]     = 0.9 + Math.random() * 0.1;
      this._colors[i3 + 1] = 0.3 + Math.random() * 0.35;
      this._colors[i3 + 2] = 0.05 + Math.random() * 0.1;

      this._sizes[idx] = EMBER_SIZE * (0.6 + Math.random() * 0.8);

      const life = EMBER_LIFETIME_MIN + Math.random() * (EMBER_LIFETIME_MAX - EMBER_LIFETIME_MIN);
      this._lifetimes[idx] = life;
      this._maxLifetimes[idx] = life;
      this._particleTypes[idx] = 1;
    }
  }

  /**
   * Update all particles.
   * @param {number} dt
   */
  _updateParticles(dt) {
    const positions = this._positions;
    const velocities = this._velocities;
    const sizes = this._sizes;
    const lifetimes = this._lifetimes;
    const maxLifetimes = this._maxLifetimes;
    const time = this._elapsed;
    const px = this._playerPos.x;
    const py = this._playerPos.y;
    const pz = this._playerPos.z;

    for (let i = 0; i < TOTAL_PARTICLES; i++) {
      if (lifetimes[i] <= 0) continue;

      lifetimes[i] -= dt;
      const i3 = i * 3;

      // Move
      positions[i3]     += velocities[i3] * dt;
      positions[i3 + 1] += velocities[i3 + 1] * dt;
      positions[i3 + 2] += velocities[i3 + 2] * dt;

      // Add subtle sinusoidal drift for organic movement
      const wobbleScale = this._particleTypes[i] === 0 ? 0.08 : 0.12;
      positions[i3]     += Math.sin(time * 1.5 + i * 2.1) * wobbleScale * dt;
      positions[i3 + 2] += Math.cos(time * 1.2 + i * 1.7) * wobbleScale * dt;

      // Fade based on lifetime
      if (lifetimes[i] <= 0) {
        sizes[i] = 0;
        positions[i3 + 1] = -1000;
      } else {
        const lifeRatio = lifetimes[i] / maxLifetimes[i];
        // Fade in over first 15%, fade out over last 25%
        let fade = 1.0;
        if (lifeRatio > 0.85) {
          fade = (1.0 - lifeRatio) / 0.15;
        } else if (lifeRatio < 0.25) {
          fade = lifeRatio / 0.25;
        }
        const baseSize = this._particleTypes[i] === 0 ? DUST_SIZE : EMBER_SIZE;
        sizes[i] = baseSize * Math.max(0.05, fade);
      }

      // Kill if too far from player (beyond spawn radius * 1.5)
      const dx = positions[i3] - px;
      const dy = positions[i3 + 1] - py;
      const dz = positions[i3 + 2] - pz;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq > DUST_SPAWN_RADIUS * DUST_SPAWN_RADIUS * 4) {
        lifetimes[i] = 0;
        sizes[i] = 0;
        positions[i3 + 1] = -1000;
      }
    }

    this._geo.getAttribute('position').needsUpdate = true;
    this._geo.getAttribute('color').needsUpdate = true;
    this._geo.getAttribute('size').needsUpdate = true;
  }

  /**
   * Kill all particles immediately.
   */
  _killAllParticles() {
    for (let i = 0; i < TOTAL_PARTICLES; i++) {
      this._lifetimes[i] = 0;
      this._sizes[i] = 0;
      this._positions[i * 3 + 1] = -1000;
    }
    this._geo.getAttribute('position').needsUpdate = true;
    this._geo.getAttribute('size').needsUpdate = true;
  }

  // ====================================================================
  // Public API
  // ====================================================================

  /**
   * Update the cave fog and dust effects.
   *
   * @param {number} dt - Delta time in seconds
   * @param {number} playerY - Player's Y position
   * @param {boolean} isUnderground - Whether player is underground
   * @param {string} currentDimension - Current dimension ('overworld' or 'nether')
   */
  update(dt, playerY, isUnderground, currentDimension) {
    this._elapsed += dt;
    this._playerPos.y = playerY;

    // We need the scene fog to exist
    if (!this.scene.fog) return;

    this._inNether = currentDimension === 'nether';

    // --- Calculate depth ratio (0 = surface threshold, 1 = bedrock) ---
    if (isUnderground) {
      // playerY ranges from ~0 (bedrock) to UNDERGROUND_Y_THRESHOLD (surface)
      this._depthRatio = Math.max(0, Math.min(1,
        1 - (playerY / UNDERGROUND_Y_THRESHOLD)
      ));
    } else {
      this._depthRatio = 0;
    }

    // --- Determine target cave factor ---
    if (this._inNether) {
      // In nether, we manage ember particles but not fog (nether has its own fog)
      this._targetCaveFactor = 0;
    } else {
      this._targetCaveFactor = isUnderground ? 1 : 0;
    }

    // --- Smooth cave factor transition ---
    const caveDiff = this._targetCaveFactor - this._caveFactor;
    if (Math.abs(caveDiff) > 0.001) {
      this._caveFactor += Math.sign(caveDiff) * Math.min(Math.abs(caveDiff), TRANSITION_SPEED * dt);
    } else {
      this._caveFactor = this._targetCaveFactor;
    }

    // --- Lava proximity detection ---
    // Simple heuristic: deep underground (Y < 15) has lava nearby
    // A proper implementation would check for nearby LAVA blocks,
    // but we keep it simple and depth-based for performance
    if (isUnderground && playerY < 15) {
      this._targetLavaFactor = Math.min(1, (15 - playerY) / 10);
    } else {
      this._targetLavaFactor = 0;
    }

    const lavaDiff = this._targetLavaFactor - this._lavaFactor;
    if (Math.abs(lavaDiff) > 0.001) {
      this._lavaFactor += Math.sign(lavaDiff) * Math.min(Math.abs(lavaDiff), TRANSITION_SPEED * dt);
    } else {
      this._lavaFactor = this._targetLavaFactor;
    }

    // --- Apply fog changes (only in overworld, not in nether) ---
    if (!this._inNether && this._caveFactor > 0.001) {
      this._managingFog = true;

      // Interpolate fog near/far based on depth
      const depthLerp = this._depthRatio;
      const targetNear = THREE.MathUtils.lerp(CAVE_FOG_NEAR_SHALLOW, CAVE_FOG_NEAR_DEEP, depthLerp);
      const targetFar = THREE.MathUtils.lerp(CAVE_FOG_FAR_SHALLOW, CAVE_FOG_FAR_DEEP, depthLerp);

      // Lerp from surface values to cave values based on caveFactor
      const fogNear = THREE.MathUtils.lerp(SURFACE_FOG_NEAR, targetNear, this._caveFactor);
      const fogFar = THREE.MathUtils.lerp(this._surfaceFogFar, targetFar, this._caveFactor);

      this.scene.fog.near = fogNear;
      this.scene.fog.far = fogFar;

      // Fog color: lerp between cave color and lava color, then lerp with surface
      this._tmpColor.copy(CAVE_FOG_COLOR);
      if (this._lavaFactor > 0.001) {
        this._tmpColor.lerp(LAVA_FOG_COLOR, this._lavaFactor);
      }

      // Lerp from surface color to computed cave color
      this._currentFogColor.copy(SURFACE_FOG_COLOR);
      this._currentFogColor.lerp(this._tmpColor, this._caveFactor);

      this.scene.fog.color.copy(this._currentFogColor);
      if (this.scene.background && this.scene.background.copy) {
        this.scene.background.copy(this._currentFogColor);
      }

    } else if (this._managingFog && this._caveFactor <= 0.001 && !this._inNether) {
      // We were managing fog but now we are back on the surface.
      // Restore surface fog. Note: DayNight system will take over from here.
      this._managingFog = false;
      // Do NOT force-reset fog here; let DayNight or main.js handle it.
      // Just stop touching the fog values.
    }

    // --- Update particles ---
    const showDust = this._caveFactor > 0.05 || this._inNether;
    const showEmbers = this._lavaFactor > 0.05 || this._inNether;

    this._points.visible = showDust || showEmbers;

    // Adjust overall particle opacity based on cave depth
    if (this._points.visible) {
      this._mat.opacity = 0.3 + this._caveFactor * 0.4;

      // Spawn dust particles when underground
      if (showDust) {
        this._dustSpawnTimer += dt;
        // Spawn rate increases with depth
        const adjustedInterval = this._dustSpawnInterval / (0.5 + this._depthRatio);
        while (this._dustSpawnTimer >= adjustedInterval) {
          this._dustSpawnTimer -= adjustedInterval;
          this._spawnDust();
        }
      }

      // Spawn ember particles near lava or in nether
      if (showEmbers) {
        this._emberSpawnTimer += dt;
        const emberInterval = this._inNether
          ? this._emberSpawnInterval * 0.6  // faster in nether
          : this._emberSpawnInterval / (0.3 + this._lavaFactor);
        while (this._emberSpawnTimer >= emberInterval) {
          this._emberSpawnTimer -= emberInterval;
          this._spawnEmber();
        }
      }

      this._updateParticles(dt);
    } else {
      this._killAllParticles();
      this._dustSpawnTimer = 0;
      this._emberSpawnTimer = 0;
    }
  }

  /**
   * Provide the player's X and Z position for particle spawning.
   * Y is already provided via the update() playerY parameter.
   * Call this every frame before update().
   *
   * @param {THREE.Vector3} playerPos - Full player position
   */
  setPlayerPosition(playerPos) {
    this._playerPos.copy(playerPos);
  }

  /**
   * Clean up all Three.js objects.
   */
  dispose() {
    if (this._points) {
      this.scene.remove(this._points);
      this._geo.dispose();
      this._mat.dispose();
      this._particleTexture.dispose();
    }
  }
}
